import { Component } from 'react';
import moment from 'moment';

import Timeline, { TimelineHeaders } from 'react-calendar-timeline';
import { sendAnalytics } from '../../helpers/analytics';
import { AutomedicTimelineView, ObservationGroupType } from './types';
import './styles.css';
import { groupEventsByMonthAndGroup } from './utils/groupEventsByWeek';
import {
  moveResizeValidator,
  verticalLineClassNamesForTime,
  generateNestedTreeView,
  getRelatedCollapsedGroups,
  updateItemsForCollapsedGroups,
  combineMonthEvents,
  computeOpenGroupIds,
  shouldShowPathologyTable,
} from './utils/timeline';
import TimelineItemRenderer from './components/items/TimelineItemRenderer';
import NoDataDisplay from './components/NoDataDisplay';
import PathologyTableButton from './components/PathologyTableButton';
import TimelineMarkersComponent from './components/TimelineMarkersComponent';
import TimelineYearHeader from './components/headers/TimelineYearHeader';
import TimelineMonthHeader from './components/headers/TimelineMonthHeader';
import TimelineDayHeader from './components/headers/TimelineDayHeader';
import TimelineHourHeader from './components/headers/TimelineHourHeader';
import TimelineToolbar from './components/toolbar/TimelineToolbar';

export default class PatientTimeline extends Component {
  constructor(props) {
    super(props);
    this.items = [];
    this.showDaysLimit = 100; //days
    this.props.items.forEach((x) => this.items.push(Object.assign({}, x)));

    const timelineEventDefaultOpen = !props.defaultCollapsed;

    const startValue = props.dashSettings?.timelineZoom?.startValue;
    const startUnit = props.dashSettings?.timelineZoom?.startUnit;

    const visibleTimeStart = moment()
      .subtract(startValue ?? 2, startUnit ?? 'month')
      .toDate()
      .valueOf();

    const visibleTimeEnd = moment().endOf('day').toDate().valueOf();

    const startDate = moment()
      .subtract(visibleTimeEnd - visibleTimeStart, 'milliseconds')
      .format();
    const days = moment().diff(startDate, 'day');

    const sd = days <= this.showDaysLimit;

    const zoom =
      moment().endOf('month').valueOf() - moment().startOf('month').valueOf();

    // Calculate minTime from the first event in the timeline
    let minTime = moment().subtract(2, 'years').startOf('month').valueOf(); // Fallback to 2 years ago
    if (this.items && this.items.length > 0) {
      const firstEventTime = Math.min(
        ...this.items.map((item) => item.start_time || Infinity),
      );
      if (firstEventTime !== Infinity) {
        minTime = moment(firstEventTime).valueOf();
      }
    }
    const maxTime = moment().endOf('month').valueOf();
    let clinician_id = this.props.clinician_id;
    // Starts with closed open
    let collapsedGroups = {};
    let openGroups = {};

    this.props.groups.forEach((g) => {
      const isParentOrRoot =
        g.root ||
        this.props.groups.some(
          (gr) => gr.parent === parseInt(g.id) && !gr.root,
        );

      if (timelineEventDefaultOpen) {
        if (isParentOrRoot) {
          collapsedGroups[g.id] = false;
          openGroups[g.id] = true;
        } else {
          collapsedGroups[g.id] = false;
        }
      } else {
        collapsedGroups[g.id] = isParentOrRoot;
      }
    });

    this.state = {
      visibleTimeStart,
      visibleTimeEnd,
      zoom,
      clinician_id,
      minTime,
      maxTime,
      showDays: sd,
      format: true,
      showHeaders: false,
      openGroups: openGroups,
      collapsedParentGroups: collapsedGroups,
      hasOpenParentGroup: false,
      items: this.items,
      viewPoint: AutomedicTimelineView.MONTH_VIEW,
      showOnlyActiveAlerts: false,
    };

    this.analyticsTimeout = null;
    this.groups = this.props.groups;
    this.isSimple = this.props.isSimple;
    this.openInsightDetails = this.props.openInsightDetails;
    this.closeInsightDetails = this.props.closeInsightDetails;
    this.setIsInsightEvent = this.props.setIsInsightEvent;
    this.setIsActionEvent = this.props.setIsActionEvent;
  }

  componentWillReceiveProps(props) {
    if (props.isSimple !== this.isSimple) {
      this.isSimple = props.isSimple;
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isSimple !== this.props.isSimple) {
      this.isSimple = this.props.isSimple;
    }

    if (prevProps.clinician_id !== this.props.clinician_id) {
      this.setState({ clinician_id: this.props.clinician_id });
    }

    if (prevProps.items !== this.props.items) {
      const nextItems = Array.isArray(this.props.items)
        ? this.props.items.map((x) => Object.assign({}, x))
        : [];
      this.items = nextItems;
      this.setState({ items: nextItems });
    }

    if (prevProps.groups !== this.props.groups) {
      const timelineEventDefaultOpen = !this.props.defaultCollapsed;
      const groupIdSet = new Set(
        (Array.isArray(this.props.groups) ? this.props.groups : []).map((g) =>
          String(g.id),
        ),
      );

      const currentOpenGroups = this.state.openGroups || {};
      const currentCollapsedGroups = this.state.collapsedParentGroups || {};

      const nextOpenGroups = Object.keys(currentOpenGroups).reduce(
        (acc, key) => {
          if (groupIdSet.has(String(key))) acc[key] = currentOpenGroups[key];
          return acc;
        },
        {},
      );

      const nextCollapsedGroups = Object.keys(currentCollapsedGroups).reduce(
        (acc, key) => {
          if (groupIdSet.has(String(key)))
            acc[key] = currentCollapsedGroups[key];
          return acc;
        },
        {},
      );

      (Array.isArray(this.props.groups) ? this.props.groups : []).forEach(
        (g) => {
          const groupKey = String(g.id);
          const isParentOrRoot =
            g.root ||
            this.props.groups.some(
              (gr) => gr.parent === parseInt(g.id) && !gr.root,
            );

          if (timelineEventDefaultOpen) {
            if (nextCollapsedGroups[groupKey] === undefined) {
              nextCollapsedGroups[groupKey] = false;
            }
            if (isParentOrRoot && nextOpenGroups[groupKey] === undefined) {
              nextOpenGroups[groupKey] = true;
            }
          } else if (nextCollapsedGroups[groupKey] === undefined) {
            nextCollapsedGroups[groupKey] = isParentOrRoot;
          }
        },
      );

      this.setState({
        openGroups: nextOpenGroups,
        collapsedParentGroups: nextCollapsedGroups,
      });
    }
  }

  toggleShowOnlyActiveAlerts = () => {
    this.setState({ showOnlyActiveAlerts: !this.state.showOnlyActiveAlerts });
  };

  handleCanvasClick = () => {
    this.closeInsightDetails();
  };

  openPathologyTable = () => {
    const pathologyItem = this.items.find(
      (item) => item.group_type === ObservationGroupType,
    );
    this.handleItemClick(pathologyItem.id);
  };

  handleItemClick = (itemId, _) => {
    if (typeof itemId === 'string' && itemId.startsWith('groupView')) {
      const group = this.groupedItemsEvents.find((obj) => obj.id === itemId);
      this.openInsightDetails(group);
      this.setIsInsightEvent(false);
      this.setIsActionEvent(false);
    } else if (
      this.items.find((obj) => obj.id === itemId).type !==
      ('lineMiddle' || 'actions')
    ) {
      if (this.items.find((obj) => obj.id === itemId).insight_id) {
        this.setIsInsightEvent(true);
        this.setIsActionEvent(false);
      } else if (this.items.find((obj) => obj.id === itemId).type == 'action') {
        this.setIsActionEvent(true);
        this.setIsInsightEvent(false);
      } else {
        this.setIsActionEvent(false);
        this.setIsInsightEvent(false);
      }
      this.openInsightDetails(this.items.find((obj) => obj.id === itemId));
    }
  };

  handleItemResize = (itemId, time, edge) => {
    this.items = this.items.map((item) =>
      item.id === itemId
        ? Object.assign({}, item, {
            start: edge === 'left' ? time : item.start,
            end: edge === 'left' ? item.end : time,
          })
        : item,
    );
  };

  // this limits the timeline to -6 months ... +6 months

  handleTimeChange = (visibleTimeStart, visibleTimeEnd) => {
    // Clamp to min/max time
    const { minTime, maxTime } = this.state;
    if (minTime && maxTime) {
      const duration = visibleTimeEnd - visibleTimeStart;
      if (visibleTimeStart < minTime) {
        visibleTimeStart = minTime;
        visibleTimeEnd = minTime + duration;
      }
      if (visibleTimeEnd > maxTime) {
        visibleTimeEnd = maxTime;
        visibleTimeStart = maxTime - duration;
      }
    }

    if (this.props.features.automedicView) {
      const duration = visibleTimeEnd - visibleTimeStart;
      const days = moment.duration(duration).asDays();
      let viewPoint = this.state.viewPoint;
      let showDays = this.state.showDays;

      if (days < 5) {
        viewPoint = AutomedicTimelineView.MULTI_DAY_VIEW;
        showDays = true;
      } else if (days < 21) {
        viewPoint = AutomedicTimelineView.WEEK_VIEW;
        showDays = true;
      } else if (days < 90) {
        viewPoint = AutomedicTimelineView.MONTH_VIEW;
        showDays = true;
      } else {
        viewPoint = AutomedicTimelineView.MULTI_MONTH_VIEW;
        showDays = false;
      }

      if (viewPoint !== this.state.viewPoint) {
        this.setState({
          visibleTimeStart,
          visibleTimeEnd,
          viewPoint,
          showDays,
        });
      } else {
        this.setState({ visibleTimeStart, visibleTimeEnd });
      }
    } else {
      this.setState({ visibleTimeStart, visibleTimeEnd });
    }

    sendAnalytics({
      name: 'Timeline Scroll',
      attributes: {
        visibleTimeStart: new Date(visibleTimeStart).toISOString(),
        visibleTimeEnd: new Date(visibleTimeEnd).toISOString(),
        showDays: JSON.stringify(this.state.showDays),
      },
    });
  };

  onThisYear = () => {
    this.setState((state) => {
      const offset =
        state.visibleTimeEnd - (state.visibleTimeStart + state.zoom);
      this.props.saveDefaultZoom(offset);

      sendAnalytics({
        name: 'This Year',
        attributes: {
          range: `365 days`,
          visibleTimeStart: moment()
            .startOf('month')
            .subtract(12, 'month')
            .toDate()
            .valueOf(),
          visibleTimeEnd: moment().endOf('day').toDate().valueOf(),
          showDays: JSON.stringify(false),
        },
      });

      return {
        visibleTimeStart: moment()
          .startOf('month')
          .subtract(12, 'month')
          .toDate()
          .valueOf(),
        visibleTimeEnd: moment().endOf('day').toDate().valueOf(),
        showDays: false,
      };
    });
  };

  onThisMonth = () => {
    this.setState((state) => {
      const offset =
        state.visibleTimeEnd - (state.visibleTimeStart + state.zoom);
      this.props.saveDefaultZoom(offset);
      sendAnalytics({
        name: 'This Month',
        attributes: {
          range: `1 month`,
          visibleTimeStart: moment()
            .startOf('month')
            .subtract(1, 'month')
            .toDate()
            .valueOf(),
          visibleTimeEnd: moment().endOf('day').toDate().valueOf(),
          showDays: JSON.stringify(false),
        },
      });
      return {
        visibleTimeStart: moment()
          .startOf('month')
          .subtract(1, 'month')
          .toDate()
          .valueOf(),
        visibleTimeEnd: moment().endOf('day').toDate().valueOf(),
        showDays: true,
        viewPoint: AutomedicTimelineView.MONTH_VIEW,
      };
    });
  };

  onThisWeek = () => {
    this.setState((state) => {
      const offset =
        state.visibleTimeEnd - (state.visibleTimeStart + state.zoom);
      this.props.saveDefaultZoom(offset);
      sendAnalytics({
        name: 'This Week',
        attributes: {
          range: `1 week`,
          visibleTimeStart: moment()
            .startOf('day')
            .subtract(7, 'day')
            .toDate()
            .valueOf(),
          visibleTimeEnd: moment().endOf('day').toDate().valueOf(),
          showDays: JSON.stringify(true),
        },
      });
      return {
        visibleTimeStart: moment()
          .startOf('day')
          .subtract(7, 'day')
          .toDate()
          .valueOf(),
        visibleTimeEnd: moment().endOf('day').toDate().valueOf(),
        showDays: true,
        viewPoint: AutomedicTimelineView.WEEK_VIEW,
      };
    });
  };

  onThisThreeDays = () => {
    this.setState((state) => {
      const offset =
        state.visibleTimeEnd - (state.visibleTimeStart + state.zoom);
      this.props.saveDefaultZoom(offset);
      sendAnalytics({
        name: 'This 3 Days',
        attributes: {
          range: `3 days`,
          visibleTimeStart: moment()
            .startOf('day')
            .subtract(2, 'day')
            .toDate()
            .valueOf(),
          visibleTimeEnd: moment().endOf('day').toDate().valueOf(),
          showDays: JSON.stringify(true),
        },
      });
      return {
        visibleTimeStart: moment()
          .startOf('day')
          .subtract(2, 'day')
          .toDate()
          .valueOf(),
        visibleTimeEnd: moment().endOf('day').toDate().valueOf(),
        showDays: true,
        viewPoint: AutomedicTimelineView.MULTI_DAY_VIEW,
      };
    });
  };

  onHalfYear = () => {
    this.setState((state) => {
      const offset =
        state.visibleTimeEnd - (state.visibleTimeStart + state.zoom);
      this.props.saveDefaultZoom(offset);
      sendAnalytics({
        name: 'Last 6 months',
        attributes: {
          range: `6 month`,
          visibleTimeStart: moment()
            .startOf('month')
            .subtract(6, 'month')
            .toDate()
            .valueOf(),
          visibleTimeEnd: moment().endOf('day').toDate().valueOf(),
          showDays: JSON.stringify(false),
        },
      });
      return {
        visibleTimeStart: moment()
          .startOf('month')
          .subtract(6, 'month')
          .toDate()
          .valueOf(),
        visibleTimeEnd: moment().endOf('day').toDate().valueOf(),
        showDays: false,
        viewPoint: AutomedicTimelineView.MULTI_MONTH_VIEW,
      };
    });
  };

  onPrevClick = () => {
    this.setState((state, props) => {
      const step = props.features.automedicView
        ? state.visibleTimeEnd - state.visibleTimeStart
        : state.zoom;

      if (state.minTime < state.visibleTimeStart - step) {
        return {
          visibleTimeStart: state.visibleTimeStart - step,
          visibleTimeEnd: state.visibleTimeEnd - step,
        };
      } else {
        return {
          visibleTimeEnd:
            state.visibleTimeEnd - (state.visibleTimeStart - state.minTime),
          visibleTimeStart: state.minTime,
        };
      }
    });
  };

  onNextClick = () => {
    this.setState((state, props) => {
      const step = props.features.automedicView
        ? state.visibleTimeEnd - state.visibleTimeStart
        : state.zoom;

      if (state.maxTime > state.visibleTimeEnd + step) {
        return {
          visibleTimeStart: state.visibleTimeStart + step,
          visibleTimeEnd: state.visibleTimeEnd + step,
        };
      } else {
        return {
          visibleTimeStart:
            state.visibleTimeStart + (state.maxTime - state.visibleTimeEnd),
          visibleTimeEnd: state.maxTime,
        };
      }
    });
  };

  onZoomIn = () => {
    if (this.props.features.automedicView) {
      // Anchor on the right side - keep visibleTimeEnd fixed
      const rightEdge = this.state.visibleTimeEnd;
      let newViewPoint;
      let timeRange;

      switch (this.state.viewPoint) {
        case AutomedicTimelineView.WEEK_VIEW:
          newViewPoint = AutomedicTimelineView.MULTI_DAY_VIEW;
          timeRange = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds
          break;
        case AutomedicTimelineView.MONTH_VIEW:
          newViewPoint = AutomedicTimelineView.WEEK_VIEW;
          timeRange = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
          break;
        case AutomedicTimelineView.MULTI_MONTH_VIEW:
          newViewPoint = AutomedicTimelineView.MONTH_VIEW;
          timeRange = 2 * 30 * 24 * 60 * 60 * 1000; // ~2 months in milliseconds
          break;
        default:
          return;
      }

      this.setState((state) => {
        const offset =
          state.visibleTimeEnd - (state.visibleTimeStart + state.zoom);
        this.props.saveDefaultZoom(offset);

        sendAnalytics({
          name: 'Zoom In AutoMedic',
          attributes: {
            range: `${timeRange / (24 * 60 * 60 * 1000)} days`,
            visibleTimeStart: new Date(rightEdge - timeRange).toISOString(),
            visibleTimeEnd: new Date(rightEdge).toISOString(),
            viewPoint: newViewPoint,
          },
        });

        return {
          visibleTimeStart: rightEdge - timeRange,
          visibleTimeEnd: rightEdge,
          showDays: newViewPoint !== AutomedicTimelineView.MULTI_MONTH_VIEW,
          viewPoint: newViewPoint,
        };
      });
      return;
    } else {
      this.setState((state) => {
        const offset =
          state.visibleTimeEnd - (state.visibleTimeStart + state.zoom);
        const startDate = moment().subtract(offset, 'milliseconds').format();
        const months = moment().diff(startDate, 'months');
        const days = moment().diff(startDate, 'day');
        if (days > 60) {
          this.props.saveDefaultZoom(offset);
          const showDays = days <= this.showDaysLimit;

          sendAnalytics({
            name: 'Zoom In',
            attributes: {
              range: `${days} days`,
              visibleTimeStart: new Date(
                state.visibleTimeStart + state.zoom,
              ).toISOString(),
              visibleTimeEnd: new Date(
                state.visibleTimeEnd - state.zoom,
              ).toISOString(),
              showDays: JSON.stringify(showDays),
            },
          });

          return {
            visibleTimeStart: state.visibleTimeStart + state.zoom,
            visibleTimeEnd: state.visibleTimeEnd - state.zoom,
            showDays: showDays,
          };
        } else {
          //show 2 months
          const start = moment(state.visibleTimeEnd)
            .subtract(2, 'months')
            .valueOf();
          const end = moment(state.visibleTimeEnd).valueOf();

          return {
            visibleTimeStart: start,
            visibleTimeEnd: end,
            showDays: true,
          };
        }
      });
    }
  };

  onZoomOut = () => {
    if (this.props.features.automedicView) {
      // Anchor on the right side - keep visibleTimeEnd fixed
      const rightEdge = this.state.visibleTimeEnd;
      let newViewPoint;
      let timeRange;

      switch (this.state.viewPoint) {
        case AutomedicTimelineView.MULTI_DAY_VIEW:
          newViewPoint = AutomedicTimelineView.WEEK_VIEW;
          timeRange = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
          break;
        case AutomedicTimelineView.WEEK_VIEW:
          newViewPoint = AutomedicTimelineView.MONTH_VIEW;
          timeRange = 2 * 30 * 24 * 60 * 60 * 1000; // ~2 months in milliseconds
          break;
        case AutomedicTimelineView.MONTH_VIEW:
          newViewPoint = AutomedicTimelineView.MULTI_MONTH_VIEW;
          timeRange = 6 * 30 * 24 * 60 * 60 * 1000; // ~6 months in milliseconds
          break;
        case AutomedicTimelineView.MULTI_MONTH_VIEW:
        default:
          return;
      }

      this.setState((state) => {
        const offset =
          state.visibleTimeEnd +
          state.zoom -
          (state.visibleTimeStart - state.zoom);
        this.props.saveDefaultZoom(offset);

        sendAnalytics({
          name: 'Zoom Out AutoMedic',
          attributes: {
            range: `${timeRange / (24 * 60 * 60 * 1000)} days`,
            visibleTimeStart: new Date(rightEdge - timeRange).toISOString(),
            visibleTimeEnd: new Date(rightEdge).toISOString(),
            viewPoint: newViewPoint,
          },
        });

        return {
          visibleTimeStart: rightEdge - timeRange,
          visibleTimeEnd: rightEdge,
          showDays: newViewPoint !== AutomedicTimelineView.MULTI_MONTH_VIEW,
          viewPoint: newViewPoint,
        };
      });
      return;
    } else {
      this.setState((state) => {
        const offset =
          state.visibleTimeEnd +
          state.zoom -
          (state.visibleTimeStart - state.zoom);
        const startDate = moment().subtract(offset, 'milliseconds').format();
        const months = moment().diff(startDate, 'months');
        const days = moment().diff(startDate, 'day');
        if (months <= 13) {
          this.props.saveDefaultZoom(offset);
          const showDays = days <= this.showDaysLimit;

          sendAnalytics({
            name: 'Zoom Out',
            attributes: {
              range: `${days} days`,
              visibleTimeStart: new Date(
                state.visibleTimeStart + state.zoom,
              ).toISOString(),
              visibleTimeEnd: new Date(
                state.visibleTimeEnd - state.zoom,
              ).toISOString(),
              showDays: JSON.stringify(showDays),
            },
          });

          return {
            visibleTimeStart: state.visibleTimeStart - state.zoom,
            visibleTimeEnd: state.visibleTimeEnd + state.zoom,
            showDays: showDays,
          };
        }
      });
    }
  };

  gotoMinTime = () => {
    this.setState((state) => {
      sendAnalytics({
        name: 'Goto Min Time',
        attributes: {
          visibleTimeStart: new Date(state.minTime).toISOString(),
          visibleTimeEnd: new Date(
            state.visibleTimeEnd - (state.visibleTimeStart - state.minTime),
          ).toISOString(),
        },
      });

      return {
        visibleTimeEnd:
          state.visibleTimeEnd - (state.visibleTimeStart - state.minTime),
        visibleTimeStart: state.minTime,
      };
    });
  };

  gotoMaxTime = () => {
    this.setState((state) => {
      // Use end of day (today) instead of maxTime (end of month)
      const today = moment().endOf('day').valueOf();
      sendAnalytics({
        name: 'Goto Max Time',
        attributes: {
          visibleTimeStart: new Date(
            state.visibleTimeStart + (today - state.visibleTimeEnd),
          ).toISOString(),
          visibleTimeEnd: new Date(today).toISOString(),
        },
      });

      return {
        visibleTimeStart:
          state.visibleTimeStart + (today - state.visibleTimeEnd),
        visibleTimeEnd: today,
      };
    });
  };

  toggleGroup = (groupId) => {
    const { openGroups, collapsedParentGroups } = this.state;
    var newOpenGroups = { ...openGroups, [groupId]: !openGroups[groupId] };
    var newCollapsedGroups = {
      ...collapsedParentGroups,
      [groupId]: openGroups[groupId],
    };
    var has = this.groups.some(
      (g) =>
        newOpenGroups[parseInt(g.id)] &&
        this.groups.some((gr) => gr.parent === g.id && !gr.root),
    );

    this.setState({
      openGroups: newOpenGroups,
      collapsedParentGroups: newCollapsedGroups,
      hasOpenParentGroup: has,
    });
  };

  itemRenderer = ({ item, getItemProps }) => {
    const clinician_id = this.state.clinician_id;
    return (
      <TimelineItemRenderer
        item={item}
        getItemProps={getItemProps}
        isSimple={this.isSimple}
        openGroups={this.state.openGroups}
        groups={this.groups}
        features={this.props.features}
        clinician_id={clinician_id}
      />
    );
  };

  groupRenderer = ({ group }) => {
    return (
      <div className="flexVertWrap">
        <div className="flexItem">{group.title}</div>
      </div>
    );
  };

  getActiveInsightItems = (items) => {
    return items
      .filter((item) => item.group_type === 'Insights')
      .filter((item) => item.action_status === null)
      .map((item) => {
        return item;
      });
  };

  render() {
    this.groups = this.props.groups;
    this.items = this.props.items;
    this.timeController = this.props.timeController;
    this.features = this.props.features;

    const {
      openGroups,
      visibleTimeStart,
      visibleTimeEnd,
      collapsedParentGroups,
      items,
    } = this.state;
    const relatedCollapsedGroups = getRelatedCollapsedGroups(
      this.groups,
      collapsedParentGroups,
    );

    const newItems = updateItemsForCollapsedGroups(
      items,
      collapsedParentGroups,
      this.groups,
      this.items,
      relatedCollapsedGroups,
    );

    // TODO: remove this if rewrite: updateNewItems is successful after testing. 3/Apr/2025

    // Object.entries(collapsedParentGroups).forEach((g) => {
    //   const rootRelatives = relatedCollapsedGroups.filter(
    //     (x) => x.rootId == g[0],
    //   );
    //   if (g[1]) {
    //     rootRelatives.forEach((r) => {
    //       const matches = newItems.filter((it) => it.group == r.id);
    //       matches.map((item) => {
    //         item.original_group = item.group;
    //         item.group = Number.parseInt(g[0]); //
    //         return item; // rollup to parent
    //       });
    //     });
    //     if (rootRelatives.length == 0) {
    //       const matches = newItems.filter(
    //         (it) => getParentGroupId(it.group) === Number.parseInt(g[0]),
    //       );
    //       matches.map((item) => {
    //         item.original_group = item.group;
    //         item.group = getParentGroupId(item.group); //
    //         return item; // rollup to parent
    //       });
    //     }
    //   } else {
    //     const matches = newItems.filter(
    //       (it) => it.group === Number.parseInt(g[0]),
    //     );
    //     matches.map((item) => {
    //       item.group = this.items.find((x) => x.id === item.id).group;
    //       return item;
    //     });
    //   }
    // });

    const newGroups = this.groups
      .filter((g) => g.root || openGroups[g.parent])
      .map((group) => {
        const hasChildren = this.groups.some(
          (gr) => gr.parent === parseInt(group.id) && !gr.root,
        );
        const howManyChildren = group.root
          ? this.groups.filter((gr) => {
              const hasChildren = this.groups.some(
                (subGroup) =>
                  subGroup.parent === parseInt(gr.id) && !subGroup.root,
              );
              return (
                gr.rootId === parseInt(group.id) && !gr.root && !hasChildren
              );
            }).length
          : 0;
        return Object.assign(
          {
            ogTitle: group.title,
            howManyChildren: howManyChildren,
          },
          group,
          {
            title: generateNestedTreeView(
              group,
              openGroups,
              hasChildren,
              howManyChildren,
              this.toggleGroup,
            ),
          },
        );
      })
      .filter((g) => !relatedCollapsedGroups.find((x) => x.id === g.id));

    const getNoInsightsItems = () => {
      return newItems.filter(
        (item) =>
          item.group_type !== 'Insights' && item.group_type !== 'Observations',
      );
    };

    const getInsightsItems = () => {
      return newItems.filter(
        (item) =>
          item.group_type === 'Insights' || item.group_type === 'Observations',
      );
    };

    const updateTimeLineItems = () => {
      const insightsItems = getInsightsItems();

      const applyEmrOrderAndHomeForWeekView = (items) => {
        const groupFlags = items.reduce((acc, item) => {
          const groupKey = item?.group;
          if (groupKey === undefined || groupKey === null) return acc;
          const existing = acc[groupKey] || {
            hasRequest: false,
            hasHome: false,
          };
          if (item?.detail?.fhir?.resourceType === 'MedicationRequest') {
            existing.hasRequest = true;
          }
          if (
            item?.detail?.homeMedication ||
            item?.detail?.fhir?.resourceType === 'MedicationDispense'
          ) {
            existing.hasHome = true;
          }
          acc[groupKey] = existing;
          return acc;
        }, {});

        return items.map((item) => {
          const flags = groupFlags[item?.group];
          if (
            flags?.hasRequest &&
            flags?.hasHome &&
            item?.type === 'emrOrder' &&
            item?.detail?.fhir?.resourceType === 'MedicationRequest'
          ) {
            return { ...item, type: 'emrOrderAndHome' };
          }
          return item;
        });
      };

      switch (this.state.viewPoint) {
        case AutomedicTimelineView.MULTI_MONTH_VIEW:
          const sameMonthsEvents =
            groupEventsByMonthAndGroup(getNoInsightsItems());
          const combinedMonth = combineMonthEvents(sameMonthsEvents);
          return [...insightsItems, ...combinedMonth];
        case AutomedicTimelineView.MONTH_VIEW:
        case AutomedicTimelineView.WEEK_VIEW:
        case AutomedicTimelineView.MULTI_DAY_VIEW:
          return applyEmrOrderAndHomeForWeekView(newItems);
        default:
          return newItems;
      }
    };

    this.groupedItemsEvents = this.props.features.automedicView
      ? updateTimeLineItems()
      : newItems;

    const findParentGroup = (groupId, acc) => {
      const targetGroup = newGroups.find((x) => x.id === groupId);
      if (targetGroup) {
        acc.add(targetGroup.id);
        if (!targetGroup.root) {
          return findParentGroup(targetGroup.parent, acc);
        }
        return targetGroup;
      }
      return null;
    };

    const activeInsightItems = this.getActiveInsightItems(newItems);

    const activeInsightItemsMap = activeInsightItems
      .map((item) => {
        return item;
      })
      .reduce((acc, cur) => {
        const groupId = cur.group;
        acc.add(groupId);
        let targetGroup = newGroups.find((x) => x.id === groupId);

        if (!targetGroup) return acc; // Ensure it exists before accessing properties

        while (targetGroup) {
          acc.add(targetGroup.id);

          targetGroup = findParentGroup(targetGroup.parent, acc);
        }

        return acc;
      }, new Set());

    const openGroupIds = computeOpenGroupIds(openGroups);

    const shouldShowPathologyTableValue = shouldShowPathologyTable(
      newGroups,
      openGroupIds,
      ObservationGroupType,
    );

    return (
      <div>
        <TimelineToolbar
          showOnlyActiveAlerts={this.state.showOnlyActiveAlerts}
          toggleShowOnlyActiveAlerts={this.toggleShowOnlyActiveAlerts}
          features={this.props.features}
          viewPoint={this.state.viewPoint}
          onZoomOut={this.onZoomOut}
          onZoomIn={this.onZoomIn}
          gotoMinTime={this.gotoMinTime}
          onPrevClick={this.onPrevClick}
          onNextClick={this.onNextClick}
          gotoMaxTime={this.gotoMaxTime}
          onThisYear={this.onThisYear}
          updateTimeLineItems={updateTimeLineItems}
          groupedItemsEvents={this.groupedItemsEvents}
          visibleTimeStart={this.state.visibleTimeStart}
          visibleTimeEnd={this.state.visibleTimeEnd}
          minTime={this.state.minTime}
          maxTime={this.state.maxTime}
        />

        {this.items.length > 0 && (
          <>
            <Timeline
              groups={newGroups.filter((group) => {
                return this.state.showOnlyActiveAlerts
                  ? activeInsightItemsMap.has(group.id)
                  : true;
              })}
              items={this.groupedItemsEvents}
              // TODO: remove this group view and items view is successful after testing. 3/Apr/2025
              // groups={newGroups}
              // items={newItems}
              sidebarWidth={250}
              sidebarContent={<div>Above The Left</div>}
              canMove={false}
              canResize="right"
              canSelect={true}
              itemsSorted
              itemTouchSendsClick={true}
              stackItems
              itemHeightRatio={1}
              lineHeight={40}
              showCursorLine
              minZoom={24 * 60 * 60 * 1000} // 1 day
              maxZoom={1.5 * 365.24 * 24 * 60 * 60 * 1000} // 1.5 years
              visibleTimeStart={visibleTimeStart}
              visibleTimeEnd={visibleTimeEnd}
              itemRenderer={this.itemRenderer}
              groupRenderer={this.groupRenderer}
              onCanvasClick={this.handleCanvasClick}
              onItemClick={this.handleItemClick}
              onItemSelect={this.handleItemClick}
              onItemResize={this.handleItemResize}
              onTimeChange={this.handleTimeChange}
              moveResizeValidator={moveResizeValidator}
              horizontalLineClassNamesForGroup={(group) =>
                group.root && !openGroups[parseInt(group.id)]
                  ? ['rowParent']
                  : ['rowChild']
              }
              verticalLineClassNamesForTime={verticalLineClassNamesForTime}
            >
              <TimelineMarkersComponent divRef={this.divRef} />

              <TimelineHeaders>
                <TimelineYearHeader onYearClick={this.onYearClick} />
                <TimelineMonthHeader onMonthClick={this.onMonthClick} />
                <TimelineDayHeader
                  features={this.props.features}
                  viewPoint={this.state.viewPoint}
                  showDays={this.state.showDays}
                />
                {this.state.viewPoint ===
                  AutomedicTimelineView.MULTI_DAY_VIEW && (
                  <TimelineHourHeader />
                )}
              </TimelineHeaders>
            </Timeline>
            {shouldShowPathologyTableValue && (
              <PathologyTableButton onClick={this.openPathologyTable} />
            )}
          </>
        )}

        {this.items.length == 0 && <NoDataDisplay />}
      </div>
    );
  }
}
